import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBrandingContext } from '../contexts/BrandingContext';
import { Icon } from './Icon';
import '../styles/AppLayout.css';

interface AppLayoutProps {
  children: React.ReactNode;
  showNav?: boolean;
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children, showNav = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { settings } = useBrandingContext();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isAdmin = user?.role === 'admin';

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Only trigger if not in an input field
      if (e.target instanceof HTMLInputElement ||
          e.target instanceof HTMLTextAreaElement ||
          e.target instanceof HTMLSelectElement) {
        return;
      }

      // 'N' key - Create new quote
      if (e.key === 'n' || e.key === 'N') {
        navigate('/quotes/new');
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [navigate]);

  const isActive = (path: string) => location.pathname === path;

  const navItems = [
    { path: '/dashboard', icon: 'home', label: 'Dashboard', admin: false },
    { path: '/quotes', icon: 'document', label: 'Quotes', admin: false },
    { path: '/clients', icon: 'user', label: 'Clients', admin: false },
    { path: '/admin/users', icon: 'users', label: 'Users', admin: true },
    { path: '/admin/materials', icon: 'package', label: 'Materials', admin: true },
    { path: '/admin/processes', icon: 'settings', label: 'Processes', admin: true },
    { path: '/admin/settings', icon: 'palette', label: 'Settings', admin: true },
  ];

  const handleNavClick = (path: string) => {
    navigate(path);
    setSidebarOpen(false); // Close mobile sidebar after navigation
  };

  return (
    <div className="app-layout">
      {showNav && (
        <>
          {/* Mobile overlay */}
          {sidebarOpen && (
            <div
              className="sidebar-overlay"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* Sidebar */}
          <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
            {/* Logo section */}
            <div className="sidebar-header">
              {settings?.branding.logoUrl ? (
                <img
                  src={settings.branding.logoUrl}
                  alt={settings.branding.logoAlt}
                  className="sidebar-logo"
                />
              ) : (
                <div className="sidebar-brand">
                  <h2>{settings?.branding.companyName || 'Quoting System'}</h2>
                  {settings?.branding.tagline && (
                    <p className="sidebar-tagline">{settings.branding.tagline}</p>
                  )}
                </div>
              )}
            </div>

            {/* Navigation */}
            <nav className="sidebar-nav">
              {navItems.map((item) => {
                if (item.admin && !isAdmin) return null;
                return (
                  <button
                    key={item.path}
                    className={`sidebar-nav-item ${isActive(item.path) ? 'active' : ''}`}
                    onClick={() => handleNavClick(item.path)}
                  >
                    <span className="nav-icon">
                      <Icon name={item.icon} size={20} />
                    </span>
                    <span className="nav-label">{item.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* User section at bottom */}
            <div className="sidebar-footer">
              <div className="sidebar-user">
                <div className="user-avatar">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <div className="user-info">
                  <div className="user-name">{user?.name}</div>
                  <div className="user-role">{user?.role}</div>
                </div>
              </div>
              <button onClick={handleLogout} className="sidebar-logout">
                <Icon name="logout" size={18} />
                <span>Sign Out</span>
              </button>
            </div>
          </aside>
        </>
      )}

      {/* Main content area */}
      <div className="main-content">
        {showNav && (
          <header className="mobile-header">
            <button
              className="hamburger-button"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            <h1 className="mobile-title">
              {settings?.branding.companyName || 'Quoting System'}
            </h1>
            <div className="mobile-user-avatar">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
          </header>
        )}
        <main className="page-content">
          {children}
        </main>
      </div>
    </div>
  );
};
